import { api, type Page } from "@/lib/api/client";

/* ------------------------------------------------------------------ types */

export type ConfigLifecycle = "DRAFT" | "ACTIVE" | "DEPRECATED" | "RETIRED";

export interface LookupType {
  id: string;
  key: string;
  moduleKey: string;
  name: string;
  description: string | null;
  allowCustomValues: boolean;
  isHierarchical: boolean;
  perLegalEntity: boolean;
  status: ConfigLifecycle;
  version: number;
}

export interface LookupValue {
  id: string;
  lookupTypeId: string;
  lookupType: { id: string; code: string | null; name: string } | null;
  code: string;
  name: string;
  parentValueId: string | null;
  legalEntityId: string | null;
  sortOrder: number;
  isDefault: boolean;
  isSystem: boolean;
  status: ConfigLifecycle;
  version: number;
}

export type FieldDataType =
  | "TEXT"
  | "LONG_TEXT"
  | "NUMBER"
  | "DATE"
  | "DATETIME"
  | "BOOLEAN"
  | "LOOKUP"
  | "MULTI_LOOKUP"
  | "REFERENCE"
  | "FILE";

export interface CustomFieldGroup {
  id: string;
  entityKey: string;
  key: string;
  name: string;
  sortOrder: number;
  classification: string;
  readPermissionKey: string | null;
  writePermissionKey: string | null;
  maskWhenDenied: boolean;
  status: ConfigLifecycle;
  version: number;
}

export interface CustomFieldDefinition {
  id: string;
  groupId: string;
  group: { id: string; code: string | null; name: string } | null;
  entityKey: string;
  key: string;
  name: string;
  helpText: string | null;
  dataType: FieldDataType;
  lookupTypeKey: string | null;
  isRequired: boolean;
  isFilterable: boolean;
  isIndexed: boolean;
  /** `NONE` | `PENDING` | `READY` | `FAILED` — whether the index is actually built. */
  indexStatus: string;
  isSortable: boolean;
  isExportable: boolean;
  showInList: boolean;
  showInForm: boolean;
  listOrder: number;
  formOrder: number;
  status: ConfigLifecycle;
  version: number;
}

export type FieldPolicyKind = "VISIBLE" | "HIDDEN" | "REQUIRED" | "READ_ONLY";

export interface FieldPolicy {
  id: string;
  entityKey: string;
  fieldKey: string;
  policy: FieldPolicyKind;
  scopeType: "SYSTEM" | "LEGAL_ENTITY";
  legalEntityId: string | null;
  reason: string | null;
  version: number;
}

export interface SystemSetting {
  id: string;
  key: string;
  value: unknown;
  isSecret: boolean;
  scopeType: string;
  scopeId: string | null;
  registered: boolean;
  description: string | null;
  version: number;
  /**
   * `false`: yalnız kurulumda belirlenen anahtar (`core.deployment.mode`) ya da kayıtta artık
   * olmayan satır — yazılamaz. 9 Ekim 2026'dan önceki sunucu göndermiyor (alan yok).
   */
  runtimeMutable?: boolean;
}

export interface StorageTarget {
  id: string;
  key: string;
  providerKind: string;
  bucket: string | null;
  prefix: string | null;
  region: string | null;
  rootPath: string | null;
  encryptionMode: string | null;
  isDefault: boolean;
  status: string;
  version: number;
}

export interface ConfigListFilters {
  page?: number | undefined;
  limit?: number | undefined;
  q?: string | undefined;
  sortBy?: string | undefined;
  sortOrder?: "asc" | "desc" | undefined;
  status?: string | undefined;
  entityKey?: string | undefined;
  groupId?: string | undefined;
  lookupTypeId?: string | undefined;
  fieldKey?: string | undefined;
  policy?: string | undefined;
  legalEntityId?: string | undefined;
  moduleKey?: string | undefined;
  providerKind?: string | undefined;
  key?: string | undefined;
}

/* ------------------------------------------------------------------ the shared shape */

function resource<TRow, TCreate, TUpdate>(base: string) {
  return {
    list: (filters: ConfigListFilters): Promise<Page<TRow>> =>
      api.page<TRow>(base, { params: { ...filters } }),
    create: (body: TCreate): Promise<TRow> => api.post<TRow>(base, body),
    update: (id: string, body: TUpdate & { version?: number }): Promise<TRow> =>
      api.patch<TRow>(`${base}/${id}`, body),
    remove: (id: string): Promise<void> => api.delete(`${base}/${id}`),
    restore: (id: string): Promise<TRow> => api.post<TRow>(`${base}/${id}/restore`),
  };
}

export interface LookupTypeInput {
  key: string;
  moduleKey: string;
  name: string;
  description?: string | undefined;
  allowCustomValues?: boolean | undefined;
  isHierarchical?: boolean | undefined;
  perLegalEntity?: boolean | undefined;
  status?: ConfigLifecycle | undefined;
}

export interface LookupValueInput {
  lookupTypeId: string;
  code: string;
  name: string;
  legalEntityId?: string | undefined;
  sortOrder?: number | undefined;
  isDefault?: boolean | undefined;
  status?: ConfigLifecycle | undefined;
}

export interface CustomFieldGroupInput {
  entityKey: string;
  key: string;
  name: string;
  classification?: string | undefined;
  readPermissionKey?: string | undefined;
  maskWhenDenied?: boolean | undefined;
  status?: ConfigLifecycle | undefined;
}

export interface CustomFieldDefinitionInput {
  groupId: string;
  entityKey: string;
  key: string;
  name: string;
  helpText?: string | undefined;
  dataType: FieldDataType;
  lookupTypeKey?: string | undefined;
  isRequired?: boolean | undefined;
  isFilterable?: boolean | undefined;
  isIndexed?: boolean | undefined;
  isSortable?: boolean | undefined;
  isExportable?: boolean | undefined;
  showInList?: boolean | undefined;
  showInForm?: boolean | undefined;
  status?: ConfigLifecycle | undefined;
}

export interface FieldPolicyInput {
  entityKey: string;
  fieldKey: string;
  policy: FieldPolicyKind;
  scopeType?: "SYSTEM" | "LEGAL_ENTITY" | undefined;
  legalEntityId?: string | undefined;
  reason?: string | undefined;
}

export interface StorageTargetInput {
  key: string;
  providerKind: string;
  bucket?: string | undefined;
  prefix?: string | undefined;
  region?: string | undefined;
  rootPath?: string | undefined;
  isDefault?: boolean | undefined;
  status?: string | undefined;
}

export const configurationApi = {
  lookupTypes: resource<LookupType, LookupTypeInput, Partial<LookupTypeInput>>(
    "/configuration/lookup-types",
  ),
  lookupValues: resource<LookupValue, LookupValueInput, Partial<LookupValueInput>>(
    "/configuration/lookup-values",
  ),
  fieldGroups: resource<CustomFieldGroup, CustomFieldGroupInput, Partial<CustomFieldGroupInput>>(
    "/configuration/custom-field-groups",
  ),
  fieldDefinitions: resource<
    CustomFieldDefinition,
    CustomFieldDefinitionInput,
    Partial<CustomFieldDefinitionInput>
  >("/configuration/custom-field-definitions"),
  storageTargets: resource<StorageTarget, StorageTargetInput, Partial<StorageTargetInput>>(
    "/configuration/storage-targets",
  ),

  /** Field policies have no restore: a policy is re-created, not resurrected. */
  fieldPolicies: {
    list: (filters: ConfigListFilters): Promise<Page<FieldPolicy>> =>
      api.page<FieldPolicy>("/configuration/field-policies", { params: { ...filters } }),
    create: (body: FieldPolicyInput): Promise<FieldPolicy> =>
      api.post<FieldPolicy>("/configuration/field-policies", body),
    update: (id: string, body: Partial<FieldPolicyInput> & { version?: number }): Promise<FieldPolicy> =>
      api.patch<FieldPolicy>(`/configuration/field-policies/${id}`, body),
    remove: (id: string): Promise<void> => api.delete(`/configuration/field-policies/${id}`),
  },

  /**
   * Settings are addressed by KEY, not by id, and written with PUT.
   *
   * The registry decides which keys exist; this endpoint stores a value against one. A key the
   * registry does not know is still readable — an old setting left behind by a downgrade — and
   * the list marks it, because deleting somebody's configuration to tidy a screen is not the
   * screen's decision to make.
   */
  settings: {
    list: (filters: ConfigListFilters): Promise<Page<SystemSetting>> =>
      api.page<SystemSetting>("/configuration/settings", { params: { ...filters } }),
    /**
     * GEREKÇE, SÜRÜM VE KAPSAM ZORUNLU (9 Ekim 2026). `upsertSettingSchema` `reason`'ı her yazmada,
     * `version`'ı var olan satırda istiyor; bu çağrı yalnız `value` gönderiyordu ve «Genel Ayarlar»
     * hiçbir ayarı kaydedemiyordu. Kapsam da satırın kendisinden: göndermeyince sunucu `SYSTEM`
     * varsayıyor ve şirkete özel bir satırı düzenleyen kişi kurulum geneli değeri değiştirirdi.
     */
    put: (
      key: string,
      value: unknown,
      input: { reason: string; version?: number | undefined; scopeType: string; scopeId: string | null },
    ): Promise<SystemSetting> =>
      api.put<SystemSetting>(`/configuration/settings/${encodeURIComponent(key)}`, {
        value,
        reason: input.reason,
        scopeType: input.scopeType,
        ...(input.scopeId ? { scopeId: input.scopeId } : {}),
        ...(typeof input.version === "number" ? { version: input.version } : {}),
      }),
  },
};
